import type { LandingContent, LandingLocale } from './landingContentTypes.js';
import type { StaticPageContent } from './staticPageContent.js';
import { applicationPortal } from './landingContentShared.js';
import teamImage from '../../img/mission/mission.jpeg';

/** A real application entry page, shared by the public site, editor and HTML snapshot. */
export const createApplicationPage = (locale: LandingLocale = 'de'): StaticPageContent => {
  const copy = {
    de: {
      eyebrow: 'Deine Bewerbung bei Viva Porta',
      title: 'Dein nächster Schritt: Arbeiten in Deutschland.',
      intro: 'Du möchtest in Deutschland arbeiten? Erzähl uns von deiner Erfahrung und deinen Zielen. Viva Porta begleitet dich von der ersten Bewerbung bis zu deinem beruflichen Start – persönlich und für Bewerber kostenfrei.',
      action: 'Jetzt zum Bewerberportal',
      highlights: ['Kostenfrei für Bewerber', 'Persönliche Begleitung'],
      ctaTitle: 'Lass uns deinen nächsten Schritt kennenlernen.',
      ctaText: 'Deine Bewerbung beginnt im Viva-Porta-Bewerberportal. Wenn du vorher etwas klären möchtest, erreichst du uns auch direkt.',
      contact: 'Noch Fragen? Kontaktiere uns',
    },
    en: {
      eyebrow: 'Your application with Viva Porta',
      title: 'Your next step: working in Germany.',
      intro: 'Would you like to work in Germany? Tell us about your experience and goals. Viva Porta guides you from your first application to your professional start – personally and free of charge for applicants.',
      action: 'Go to the applicant portal',
      highlights: ['Free for applicants', 'Personal guidance'],
      ctaTitle: 'Let’s explore your next step.',
      ctaText: 'Start your application in the Viva Porta applicant portal. If you have questions beforehand, you can also contact us directly.',
      contact: 'Questions? Contact us',
    },
    tr: {
      eyebrow: 'Viva Porta ile başvurun',
      title: 'Bir sonraki adımın: Almanya’da çalışmak.',
      intro: 'Almanya’da çalışmak mı istiyorsun? Deneyimlerini ve hedeflerini bize anlat. Viva Porta, ilk başvurundan iş hayatına başlangıcına kadar sana kişisel olarak ve adaylar için ücretsiz destek olur.',
      action: 'Aday portalına git',
      highlights: ['Adaylar için ücretsiz', 'Kişisel rehberlik'],
      ctaTitle: 'Bir sonraki adımını birlikte planlayalım.',
      ctaText: 'Başvurun Viva Porta aday portalında başlar. Öncesinde bir sorunu netleştirmek istersen bize doğrudan ulaşabilirsin.',
      contact: 'Soruların mı var? Bize ulaş',
    },
  }[locale];
  return {
    key: 'bewerben', heroTitleHighlight: applicationHeroHighlight(locale), eyebrow: copy.eyebrow, title: copy.title, intro: copy.intro,
    heroImage: teamImage, heroAction: { label: copy.action, href: applicationPortal.overview },
    heroHighlights: copy.highlights, cards: [], sections: [],
    cta: {
      eyebrow: '', title: copy.ctaTitle, text: copy.ctaText,
      primaryAction: { label: copy.action, href: applicationPortal.overview },
      secondaryAction: { label: copy.contact, href: '/kontakt' },
    },
  };
};

export const applicationHeroHighlight = (locale: LandingLocale): string => ({ de: 'Arbeiten', en: 'working', tr: 'çalışmak' })[locale];

/** Shares the homepage FAQ, including editorial changes, with the application page. */
export const applicationPagePresentation = (page: StaticPageContent, content: LandingContent): StaticPageContent => ({
  ...page,
  heroTitleHighlight: page.heroTitleHighlight ?? applicationHeroHighlight(content.locale),
  sections: [],
  faq: content.faqApplicant.items,
});
